import { CHARTER_EMAIL_KINDS, CHARTER_EMAIL_LABELS, type CharterEmailKind } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@sea-snag/ui';
import { createFileRoute } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { ChevronLeft, ChevronRight, Phone, PhoneCall, Star, StickyNote } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../components/confirm-dialog';
import {
  useAdminCharters,
  useCharterCountries,
  useSendCharterEmail,
  useSetCharterCalled,
  useSetCharterNote,
} from '../../hooks/use-admin';
import { useDebounce } from '../../hooks/use-debounce';
import type { AdminCharter } from '../../lib/api-client';
import { confirmEmailCopy, emailCaption, emailName } from '../../lib/charter-emails';

export const Route = createFileRoute('/admin/charters')({
  component: AdminChartersPage,
});

const ALL_COUNTRIES = 'all';

async function sendErrorMessage(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.json()) as { error?: { message?: string } };
      if (body?.error?.message) return body.error.message;
    } catch {
      // ignore parse errors
    }
  }
  return 'Failed to send the email';
}

function AdminChartersPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [country, setCountry] = useState(ALL_COUNTRIES);
  const [invited, setInvited] = useState('all');
  const [gold, setGold] = useState('all');
  const [page, setPage] = useState(1);
  const [pendingEmail, setPendingEmail] = useState<{
    charter: AdminCharter;
    kind: CharterEmailKind;
  } | null>(null);
  const [noteTarget, setNoteTarget] = useState<AdminCharter | null>(null);
  const limit = 20;

  const { data, isLoading, isError } = useAdminCharters({
    search: debouncedSearch || undefined,
    country: country === ALL_COUNTRIES ? undefined : country,
    invited,
    gold,
    page,
    limit,
  });
  const { data: countries } = useCharterCountries();
  const sendEmail = useSendCharterEmail();
  const setNote = useSetCharterNote();
  const setCalled = useSetCharterCalled();

  const charters = data?.data ?? [];
  const meta = data?.meta;

  function resetToFirstPage<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function confirmEmail() {
    if (!pendingEmail) return;
    const { charter, kind } = pendingEmail;

    sendEmail.mutate(
      { pid: charter.pid, kind },
      {
        onSuccess: (result) =>
          toast.success(`${CHARTER_EMAIL_LABELS[kind]} sent to ${result.email}`),
        onError: (error) => {
          void sendErrorMessage(error).then((message) => toast.error(message));
        },
      }
    );
    setPendingEmail(null);
  }

  const pendingCopy = pendingEmail
    ? confirmEmailCopy(pendingEmail.kind, pendingEmail.charter)
    : null;

  function saveNote(charter: AdminCharter, note: string | null) {
    setNote.mutate(
      { pid: charter.pid, note },
      {
        // Close only once the save has landed. Closing on submit would discard
        // whatever was typed if the request fails, leaving a toast and no way
        // to retry the text — and the saving state would never be visible.
        onSuccess: () => {
          toast.success(note ? 'Note saved' : 'Note cleared');
          setNoteTarget(null);
        },
        onError: () => toast.error('Could not save the note — your text is still here'),
      }
    );
  }

  function toggleCalled(charter: AdminCharter) {
    const called = charter.calledAt === null;
    setCalled.mutate(
      { pid: charter.pid, called },
      {
        onSuccess: () => toast.success(called ? 'Marked as called' : 'Call record cleared'),
        onError: () => toast.error('Could not update the call record'),
      }
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Charters</h1>
        <p className="text-muted-foreground">
          Charter companies from the market feed — invite them to Sea Snag.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All charters {meta ? `(${meta.total})` : ''}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Input
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => resetToFirstPage(setSearch)(e.target.value)}
              className="max-w-xs"
            />
            <Select value={country} onValueChange={resetToFirstPage(setCountry)}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_COUNTRIES}>All countries</SelectItem>
                {(countries ?? []).map((c) => (
                  <SelectItem key={c.country} value={c.country}>
                    {c.country} ({c.total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={invited} onValueChange={resetToFirstPage(setInvited)}>
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Invited: any</SelectItem>
                <SelectItem value="no">Not invited</SelectItem>
                <SelectItem value="yes">Already invited</SelectItem>
              </SelectContent>
            </Select>
            <Select value={gold} onValueChange={resetToFirstPage(setGold)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Gold: any</SelectItem>
                <SelectItem value="yes">Gold partners</SelectItem>
                <SelectItem value="no">Not gold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Could not load charters.
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Called</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">Emails</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {charters.map((charter) => (
                    <TableRow key={charter.pid}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{charter.name}</span>
                          {charter.isGoldPartner && <GoldPartnerBadge />}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {charter.country ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{charter.city ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {charter.email ?? <span className="italic">no address</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {charter.phones.length === 0 ? (
                          '—'
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            {charter.phones.map((phone) => (
                              <CallLink key={phone} phone={phone} />
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <CalledCell
                          charter={charter}
                          isPending={
                            setCalled.isPending && setCalled.variables?.pid === charter.pid
                          }
                          onToggle={() => toggleCalled(charter)}
                        />
                      </TableCell>
                      <TableCell>
                        <NoteCell charter={charter} onOpen={() => setNoteTarget(charter)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <EmailsCell
                          charter={charter}
                          pendingKind={
                            sendEmail.isPending && sendEmail.variables?.pid === charter.pid
                              ? sendEmail.variables.kind
                              : null
                          }
                          onSend={(kind) => setPendingEmail({ charter, kind })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {charters.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                        No charters match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {meta && meta.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {meta.page} of {meta.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="mr-1 size-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= meta.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                      <ChevronRight className="ml-1 size-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingEmail !== null}
        onOpenChange={(open) => {
          if (!open) setPendingEmail(null);
        }}
        title={pendingCopy?.title ?? ''}
        description={pendingCopy?.description ?? ''}
        confirmLabel={pendingCopy?.confirmLabel ?? 'Send'}
        variant="destructive"
        onConfirm={confirmEmail}
      />

      {noteTarget && (
        // Keyed on the charter so the draft resets when a different row is
        // opened, rather than carrying the previous charter's text over.
        <NoteDialog
          key={noteTarget.pid}
          charter={noteTarget}
          isSaving={setNote.isPending}
          onClose={() => setNoteTarget(null)}
          onSave={(note) => saveNote(noteTarget, note)}
        />
      )}
    </div>
  );
}

function GoldPartnerBadge() {
  return (
    <Badge
      variant="secondary"
      className="border-amber-300 bg-amber-100 font-normal text-amber-900 dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-200"
    >
      <Star className="mr-1 size-3 fill-current" />
      Gold Partner
    </Badge>
  );
}

function CalledCell({
  charter,
  isPending,
  onToggle,
}: {
  charter: AdminCharter;
  isPending: boolean;
  onToggle: () => void;
}) {
  const called = charter.calledAt !== null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onToggle}
      disabled={isPending}
      title={called ? 'Clear the call record' : 'Mark as called'}
      className={called ? 'text-foreground' : 'text-muted-foreground'}
    >
      <PhoneCall className={`mr-1 size-4 ${called ? 'text-emerald-600' : ''}`} />
      {called && charter.calledAt ? new Date(charter.calledAt).toLocaleDateString() : 'Mark called'}
    </Button>
  );
}

/**
 * The list only ever shows whether a note exists — the text itself would make
 * rows jump around in height. Clicking opens it.
 */
function NoteCell({ charter, onOpen }: { charter: AdminCharter; onOpen: () => void }) {
  const hasNote = charter.note !== null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onOpen}
      title={hasNote ? 'Read or edit the note' : 'Add a note'}
      className={hasNote ? 'text-foreground' : 'text-muted-foreground'}
    >
      <StickyNote className={`mr-1 size-4 ${hasNote ? 'text-amber-600' : ''}`} />
      {hasNote ? 'Note' : 'Add'}
    </Button>
  );
}

function NoteDialog({
  charter,
  isSaving,
  onClose,
  onSave,
}: {
  charter: AdminCharter;
  isSaving: boolean;
  onClose: () => void;
  onSave: (note: string | null) => void;
}) {
  const [draft, setDraft] = useState(charter.note ?? '');
  const trimmed = draft.trim();
  const unchanged = trimmed === (charter.note ?? '').trim();

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Note — {charter.name}</DialogTitle>
          <DialogDescription>
            Contact person, outcome of a call, or the date to follow up on. Only visible to admins.
          </DialogDescription>
        </DialogHeader>

        <Textarea
          autoFocus
          rows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Spoke to Ivan — call back after 15 Oct."
          // The dialog title names the charter visually, but a placeholder is
          // not an accessible name — without this the field is announced as an
          // unlabelled text area.
          aria-label={`Note for ${charter.name}`}
        />

        <DialogFooter>
          {charter.note !== null && (
            <Button
              variant="ghost"
              onClick={() => onSave(null)}
              disabled={isSaving}
              className="mr-auto"
            >
              Clear note
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => onSave(trimmed || null)} disabled={isSaving || unchanged}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One button per email, each with how its last attempt ended underneath. */
function EmailsCell({
  charter,
  pendingKind,
  onSend,
}: {
  charter: AdminCharter;
  pendingKind: CharterEmailKind | null;
  onSend: (kind: CharterEmailKind) => void;
}) {
  if (!charter.email) {
    return <span className="text-xs text-muted-foreground">no address</span>;
  }

  return (
    <div className="flex items-start justify-end gap-1.5">
      {CHARTER_EMAIL_KINDS.map((kind) => {
        const caption = emailCaption(charter.emails[kind]);
        const isPending = pendingKind === kind;
        return (
          <div key={kind} className="flex flex-col items-center gap-1">
            <Button
              variant="outline"
              size="xs"
              onClick={() => onSend(kind)}
              disabled={isPending}
              aria-label={`Send the ${emailName(kind)} to ${charter.name}`}
            >
              {isPending ? 'Sending…' : CHARTER_EMAIL_LABELS[kind]}
            </Button>
            {caption && (
              <span
                className={
                  caption.tone === 'error'
                    ? 'whitespace-nowrap text-[11px] text-destructive'
                    : 'whitespace-nowrap text-[11px] text-muted-foreground'
                }
                title={caption.title}
              >
                {caption.text}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Click-to-call. The displayed text keeps whatever formatting the charter gave
 * us, while the href is stripped to digits — spaces and brackets read fine but
 * stop a phone dialling.
 */
function CallLink({ phone }: { phone: string }) {
  const dialable = phone.replace(/[^\d+]/g, '');
  if (!/\d/.test(dialable)) return <span>{phone}</span>;
  return (
    <a
      href={`tel:${dialable}`}
      className="hover:text-foreground inline-flex items-center gap-1 whitespace-nowrap hover:underline"
    >
      <Phone className="size-3 shrink-0" />
      {phone}
    </a>
  );
}
